import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar,
  DollarSign,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Star,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';
import { projectAPI, projectTypeAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import { useCurrency } from '../../contexts/CurrencyContext';

const ProjectsManagement = () => {
  const { notify } = useNotificationHook();
  const { formatPrice } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(10);
  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bulk actions state
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch projects and project types on component mount
  useEffect(() => {
    fetchProjects();
    fetchProjectTypes();
  }, []);

  const fetchProjects = async () => {
    try {
      setFetchLoading(true);
      setError(null);
      
      const response = await projectAPI.getAll({
        page: currentPage,
        limit: projectsPerPage,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        project_type: filterType !== 'all' ? filterType : undefined,
        search: searchTerm || undefined
      });

      if (response?.data?.success) {
        const projectsData = response.data.data.projects || [];
        
        // Parse JSON fields
        const formattedProjects = projectsData.map(project => {
          try {
            return {
              ...project,
              features: typeof project.features === 'string' ? JSON.parse(project.features) : (project.features || []),
              testimonials: project.testimonials || '' // testimonials is now plain text, not JSON
            };
          } catch (parseError) {
            console.error('Error parsing project:', project, parseError);
            return {
              ...project,
              features: [],
              testimonials: project.testimonials || '' // testimonials is now plain text, not JSON
            };
          }
        });
        
        setProjects(formattedProjects);
      } else {
        console.error('Failed to fetch projects:', response);
        setError('Failed to load projects');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(`Error loading projects: ${err.message}`);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await projectTypeAPI.getAll();
      
      if (response?.data?.success) {
        setProjectTypes(response.data.data.projectTypes || []);
      } else {
        console.error('Failed to fetch project types:', response);
      }
    } catch (err) {
      console.error('Error fetching project types:', err);
    }
  };

  // Refetch projects when filters change
  useEffect(() => {
    fetchProjects();
  }, [currentPage, filterStatus, filterType, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAddProject = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      // Get current user ID from localStorage or context
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id;

      console.log('🔍 User data:', user);
      console.log('🔍 User ID:', userId);

      if (!userId) {
        throw new Error('User not authenticated');
      }

      const requestData = {
        ...formData,
        user_id: userId
      };
      
      console.log('🔍 Creating project with data:', requestData);
      console.log('🔍 Request data keys:', Object.keys(requestData));
      
      const response = await projectAPI.create(requestData);

      if (response?.data?.success) {
        // Refetch projects to get updated list
        await fetchProjects();
        setShowAddModal(false);
      } else {
        throw new Error(response?.data?.message || 'Failed to create project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      console.error('Error response:', err.response?.data);
      setError(`Error creating project: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await projectAPI.update(selectedProject.id, formData);

      if (response?.data?.success) {
        // Refetch projects to get updated list
        await fetchProjects();
        setShowEditModal(false);
        setSelectedProject(null);
      } else {
        throw new Error(response?.data?.message || 'Failed to update project');
      }
    } catch (err) {
      console.error('Error updating project:', err);
      console.error('Error response:', err.response?.data);
      console.error('Validation errors:', err.response?.data?.errors);
      if (err.response?.data?.errors) {
        console.error('First validation error:', err.response.data.errors[0]);
      }
      setError(`Error updating project: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        setLoading(true);
        setError(null);

        const response = await projectAPI.delete(projectId);

        if (response?.data?.success) {
          // Refetch projects to get updated list
          await fetchProjects();
        } else {
          throw new Error(response?.data?.message || 'Failed to delete project');
        }
      } catch (err) {
        console.error('Error deleting project:', err);
        setError(`Error deleting project: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const totalProjects = projects.length;
  const totalPages = Math.ceil(totalProjects / projectsPerPage);

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'completed':
        return `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`;
      case 'in_progress':
        return `${baseClasses} text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20`;
      case 'inquiry':
        return `${baseClasses} text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20`;
      case 'quoted':
        return `${baseClasses} text-purple-700 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20`;
      case 'approved':
        return `${baseClasses} text-orange-700 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/20`;
      case 'cancelled':
        return `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'inquiry':
        return <AlertCircle className="h-4 w-4" />;
      case 'quoted':
        return <FileText className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  // Filter projects based on search and filters
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (project.client_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const projectType = project.type || project.project_type;
    const matchesType = filterType === 'all' || projectType === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Bulk action functions
  const handleSelectProject = (projectId) => {
    setSelectedProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProjects([]);
      setSelectAll(false);
    } else {
      setSelectedProjects(projects.map(project => project.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedProjects.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await projectAPI.bulkUpdateStatus(selectedProjects, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedProjects.length} projects to ${status} status`);
        setSelectedProjects([]);
        setSelectAll(false);
        await fetchProjects();
      } else {
        notify.error('Failed to update projects: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating projects:', error);
      notify.error('Error updating projects: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkFeaturedUpdate = async (featured) => {
    if (selectedProjects.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await projectAPI.bulkUpdateFeatured(selectedProjects, featured);
      
      if (response.data.success) {
        notify.success(`Successfully ${featured ? 'set' : 'removed'} featured status for ${selectedProjects.length} projects`);
        setSelectedProjects([]);
        setSelectAll(false);
        await fetchProjects();
      } else {
        notify.error('Failed to update projects: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating projects:', error);
      notify.error('Error updating projects: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedProjects.length} projects? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await projectAPI.bulkDelete(selectedProjects);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedProjects.length} projects`);
        setSelectedProjects([]);
        setSelectAll(false);
        await fetchProjects();
      } else {
        notify.error('Failed to delete projects: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting projects:', error);
      notify.error('Error deleting projects: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedProjects.length > 0);
  }, [selectedProjects]);

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Projects Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Manage client projects and track progress
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Add Project</span>
            <span className="sm:hidden">Add Project</span>
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedProjects.length} project{selectedProjects.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('inquiry')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                  Mark Inquiry
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('quoted')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                  Mark Quoted
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('approved')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Mark Approved
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('in_progress')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Clock className="h-3 w-3 mr-1" />}
                  Mark In Progress
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('completed')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Mark Completed
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('cancelled')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}
                  Mark Cancelled
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Mark Featured
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Remove Featured
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:p-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalProjects}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {projects.filter(p => p.status === 'in_progress').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {projects.filter(p => p.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inquiry</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {projects.filter(p => p.status === 'inquiry').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-48 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="inquiry">Inquiry</option>
              <option value="quoted">Quoted</option>
              <option value="approved">Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full sm:w-48 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="website">Website</option>
              <option value="mobile_app">Mobile App</option>
              <option value="web_app">Web App</option>
              <option value="ecommerce">E-commerce</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center"
                  >
                    {selectAll ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {fetchLoading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-red-600 dark:text-red-400">
                      {error}
                    </td>
                  </tr>
                ) : filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No projects found
                    </td>
                  </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleSelectProject(project.id)}
                        className="flex items-center"
                      >
                        {selectedProjects.includes(project.id) ? (
                          <CheckSquare className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Square className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                          {getStatusIcon(project.status)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {project.title}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {(project.type || project.project_type || 'Project').replace('_', ' ')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        <div className="text-sm text-gray-900 dark:text-white">
                          {project.client_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(project.status)}>
                        {project.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 dark:text-white">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {project.budget ? formatPrice(project.budget) : 'N/A'}
                      </div>
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center">
                         {[...Array(5)].map((_, i) => (
                           <Star
                             key={i}
                             className={`h-3 w-3 ${
                               i < (project.star_rating || 5)
                                 ? 'text-yellow-400 fill-current'
                                 : 'text-gray-300'
                             }`}
                           />
                         ))}
                       </div>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center space-x-2">
                         {project.is_active ? (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                             Active
                           </span>
                         ) : (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                             Inactive
                           </span>
                         )}
                         {project.featured && (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                             <Star className="h-3 w-3 mr-1" />
                             Featured
                           </span>
                         )}
                       </div>
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(project)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <ProjectModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddProject}
          title="Add New Project"
          loading={loading}
          projectTypes={projectTypes}
        />
      )}

      {/* Edit Project Modal */}
      {showEditModal && selectedProject && (
        <ProjectModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProject(null);
          }}
          onSubmit={handleEditProject}
          title="Edit Project"
          project={selectedProject}
          loading={loading}
          projectTypes={projectTypes}
        />
      )}
    </div>
  );
};

// Project Modal Component
const ProjectModal = ({ isOpen, onClose, onSubmit, title, project = null, loading = false, projectTypes = [] }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'inquiry',
    client_name: '',
    client_email: '',
    budget: '',
    deadline: '',
    requirements: '',
    live_url: '',
    featured: false,
    testimonials: '',
    image: '',
    star_rating: 5,
    is_active: true,
    project_type_id: null
  });

  // Update form data when project prop changes (for editing)
  useEffect(() => {
    if (project) {
      console.log('🔍 Loading project data:', project);
      console.log('🔍 Project testimonials:', project.testimonials);
      setFormData({
        title: project.title || '',
        description: project.description || '',
        status: project.status || 'inquiry',
        client_name: project.client_name || '',
        client_email: project.client_email || '',
        budget: project.budget || '',
        deadline: project.deadline || project.timeline || '',
        requirements: project.requirements || '',
        live_url: project.live_url || '',
        featured: project.featured || false,
        testimonials: project.testimonials || '', // testimonials is now plain text
        image: project.image || '',
        star_rating: project.star_rating || 5,
        is_active: project.is_active !== undefined ? project.is_active : true,
        project_type_id: project.project_type_id || null
      });
    } else {
      // Reset form for new project
      setFormData({
        title: '',
        description: '',
        status: 'inquiry',
        client_name: '',
        client_email: '',
        budget: '',
        deadline: '',
        requirements: '',
        live_url: '',
        featured: false,
        testimonials: '',
        image: '',
        star_rating: 5,
        is_active: true,
        project_type_id: null
      });
    }
  }, [project]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Map form data to API expected format
    const { deadline, budget, featured, star_rating, is_active, project_type_id, ...restFormData } = formData;
    const apiData = {
      ...restFormData,
      timeline: deadline, // Map 'deadline' to 'timeline' for API
      budget: budget ? parseFloat(budget) : null, // Convert budget to number
      featured: Boolean(featured), // Ensure featured is boolean
      star_rating: parseInt(star_rating) || 5, // Convert star rating to number
      is_active: Boolean(is_active), // Ensure is_active is boolean
      project_type_id: project_type_id && project_type_id !== '' ? parseInt(project_type_id) : null // Convert project_type_id to number
    };
          console.log('🔍 Mapped API data:', apiData);
      console.log('🔍 Testimonials value:', apiData.testimonials);
      console.log('🔍 Features value:', apiData.features);
      console.log('🔍 Budget value:', apiData.budget, typeof apiData.budget);
      console.log('🔍 Project type ID value:', apiData.project_type_id, typeof apiData.project_type_id);
      console.log('🔍 Live URL value:', apiData.live_url);
      console.log('🔍 Featured value:', apiData.featured, typeof apiData.featured);
      console.log('🔍 Star rating value:', apiData.star_rating, typeof apiData.star_rating);
      console.log('🔍 Is active value:', apiData.is_active, typeof apiData.is_active);
      onSubmit(apiData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    console.log('🔍 Form field change:', { name, value, type, checked, newValue });
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{title}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client Name
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client Email
              </label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="inquiry">Inquiry</option>
                <option value="quoted">Quoted</option>
                <option value="approved">Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Budget
              </label>
              <input
                type="number"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Deadline
              </label>
              <input
                type="date"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Live URL
            </label>
            <input
              type="url"
              name="live_url"
              value={formData.live_url}
              onChange={handleChange}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Featured Project
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Requirements
            </label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client Testimonial
            </label>
            <textarea
              name="testimonials"
              value={formData.testimonials}
              onChange={handleChange}
              rows="3"
              placeholder="Enter client testimonial..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project Image URL
            </label>
            <input
              type="url"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Star Rating
              </label>
              <select
                name="star_rating"
                value={formData.star_rating}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project Type Category
              </label>
              <select
                name="project_type_id"
                value={formData.project_type_id || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Project Type</option>
                {projectTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Active (Show on Website)
            </label>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : project ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectsManagement;
